import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import type { UserDto, UserExtendedDto } from "@/api/types";
import { IconButton } from "@/components/Button/IconButton";
import { XCloseIcon } from "@/components/Icons/XCloseIcon";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { User } from "@/components/User/User";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { Checkbox } from "@/design-system/Checkbox";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useDebounceValue } from "@/hooks/useDebounce";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { MIN_GROUP_SEARCH_CHARACTERS } from "@/modules/chats/components/CreateChatModal";
import { chatMutations } from "@/queries/chats/mutations";
import { useChatQueries } from "@/queries/chats/queryOptions";
import { useTranslation } from "@/translations";

import type { EditGroupChatFormValues } from "./EditGroupChatModal";

interface EditGroupChatModalAddMembersViewProps {
  chatId: string;
  isOpen: boolean;
  onNavigateBack: () => void;
}

export function EditGroupChatModalAddMembersView({
  chatId,
  isOpen,
  onNavigateBack,
}: EditGroupChatModalAddMembersViewProps): React.ReactNode {
  const [query, setQuery] = useState<string>("");

  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const chatQueries = useChatQueries();

  const formMethods = useFormContext<EditGroupChatFormValues>();
  const debouncedQuery = useDebounceValue(query.trim().length < MIN_GROUP_SEARCH_CHARACTERS ? "" : query);

  const { data: members, isPending: isPendingMembers } = useQuery({
    ...chatQueries.chatMembers({ chatId }),
    enabled: isOpen,
  });
  const {
    data: availableUsersData,
    hasNextPage: hasMoreUsers,
    fetchNextPage: fetchMoreUsers,
    isLoading: isLoadingUsers,
  } = useInfiniteQuery({
    ...chatQueries.listAvailableUsersInfinite(debouncedQuery),
    enabled: isOpen,
  });
  const addMembersMutation = chatMutations.useAddGroupChatMembers(chatId);

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setQuery("");

    return () => setQuery("");
  }, []);

  const onClickRemoveSelectedUser = (user: UserDto) => {
    const selectedUsers = formMethods.getValues("newMembers");

    formMethods.setValue(
      "newMembers",
      selectedUsers.filter((selectedUser) => selectedUser.id !== user.id),
    );
  };

  const onClickConfirmAddMembers = async () => {
    await addMembersMutation.mutateAsync(
      { userIds: selectedUsers.map((selectedUser) => selectedUser.id) },
      {
        onSuccess: () => {
          onNavigateBack();
        },
      },
    );

    formMethods.setValue("newMembers", []);
  };

  const availableUsers = useMemo(
    () => availableUsersData?.pages.flatMap((page) => page.items) ?? [],
    [availableUsersData],
  );
  const selectedUsers = formMethods.watch("newMembers");
  const existingMembers = members || [];
  const amountTotalUsers = availableUsersData?.pages[0].total || 0;

  return (
    <section className="flex w-full flex-1 flex-col gap-space-lg">
      {/* Search */}
      <SearchInput
        data-testid="search-input"
        onChange={(e) => setQuery(e.target.value.trim())}
        placeholder={
          sessionUser.project.type === "addressBased"
            ? t("page.chats.modal.new-chat.input-placeholder.address-based")
            : t("page.chats.modal.new-chat.input-placeholder.company-based")
        }
      />
      {/* Newly selected users */}
      {selectedUsers.length > 0 && (
        <Card borderTone="secondary" density="flush">
          <ul className="flex h-fit max-w-full gap-space-lg overflow-x-auto p-space-md">
            {selectedUsers.map((user) => (
              <Stack as="li" key={user.id} gap="sm" align="center">
                <span className="relative">
                  <UserAvatar key={user.id} image={user.avatar} />
                  <span className="absolute top-0 right-0 flex size-icon-prominent translate-x-1/4 -translate-y-1/4 items-center justify-center [&>button]:size-full!">
                    <IconButton
                      title={t("common.action.delete")}
                      onClick={() => onClickRemoveSelectedUser(user)}
                      variant="filled"
                      tone="error"
                      withTooltip={false}
                      size="xs"
                      isCircular
                      icon={<XCloseIcon />}
                    />
                  </span>
                </span>
                <span className="text-overline">{user.firstName}</span>
              </Stack>
            ))}
          </ul>
        </Card>
      )}
      <EditGroupChatModalAddMembersListView
        members={existingMembers}
        users={availableUsers}
        isLoading={isLoadingUsers || isPendingMembers}
        isMoreUsersAvailable={hasMoreUsers}
        onLoadMoreUsers={fetchMoreUsers}
        {...{ amountTotalUsers }}
      />
      <footer className="flex w-full flex-col-reverse gap-space-sm *:w-full @md:flex-row @md:items-center @md:justify-end @md:*:w-fit">
        <Button variant="soft" onClick={onNavigateBack}>
          {t("common.action.back")}
        </Button>

        <Button
          variant="filled"
          isLoading={addMembersMutation.isPending}
          disabled={selectedUsers.length === 0}
          onClick={onClickConfirmAddMembers}
        >
          {t("common.action.confirm")}
        </Button>
      </footer>
    </section>
  );
}

interface EditGroupChatModalAddMembersListViewProps {
  members: UserExtendedDto[];
  users: UserExtendedDto[];
  amountTotalUsers: number;
  isLoading: boolean;
  isMoreUsersAvailable: boolean;
  onLoadMoreUsers: () => void;
}

const EditGroupChatModalAddMembersListView = ({
  members,
  users,
  amountTotalUsers,
  isLoading,
  isMoreUsersAvailable,
  onLoadMoreUsers,
}: EditGroupChatModalAddMembersListViewProps) => {
  const { t } = useTranslation();
  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      onLoadMoreUsers();
    }, [onLoadMoreUsers]),
  });
  const formMethods = useFormContext<EditGroupChatFormValues>();
  const selectedUsers = useWatch<EditGroupChatFormValues, "newMembers">({ name: "newMembers" });

  const onToggleSelectUser = (isSelected: boolean, user: UserExtendedDto) => {
    const currSelectedUser = formMethods.getValues("newMembers");

    let newSelectedUsers = [...currSelectedUser];
    if (isSelected) {
      newSelectedUsers.push(user);
    } else {
      newSelectedUsers = newSelectedUsers.filter((selectedUser) => selectedUser.id !== user.id);
    }

    formMethods.setValue("newMembers", newSelectedUsers);
  };

  return (
    <section className="flex size-full flex-col gap-space-sm">
      <span className="text-caption-emphasis">
        {t("page.chats.modal.new-chat.total-results", { count: amountTotalUsers })}
      </span>
      <section className="flex h-scroll-area-compact w-full flex-col items-center justify-center">
        {/* Loading state */}
        {isLoading && <Spinner size="md" />}
        {/* Empty state */}
        {!isLoading && amountTotalUsers === 0 && (
          <p className="text-caption text-text-secondary">{t("page.chats.modal.new-chat.no-results")}</p>
        )}
        {/* Contentful state */}
        {!isLoading && amountTotalUsers > 0 && (
          <Card as="ul" density="flush" borderTone="secondary" scroll="content" height="full">
            {users.map((user) => {
              const isUserMember = members.some((member) => member.id === user.id);
              const isUserSelected = selectedUsers.some((selectedUser) => selectedUser.id === user.id);

              return (
                <li
                  key={user.id}
                  data-testid="available-user"
                  className="cursor-pointer border-b border-b-border-secondary last:border-b-0 hover:bg-background-brand-light"
                >
                  <label
                    htmlFor={`user-${user.id}`}
                    className="flex w-full cursor-pointer items-center justify-between p-space-md"
                  >
                    <User user={user} renderSubtitle={() => user.locatedAt} shouldNavigate={false} />
                    <Checkbox
                      id={`user-${user.id}`}
                      name="groupMembers"
                      checked={isUserMember || isUserSelected}
                      onChange={(e) => onToggleSelectUser(e.target.checked, user)}
                      shape="rounded"
                      disabled={isUserMember}
                    />
                  </label>
                </li>
              );
            })}
            {isMoreUsersAvailable && (
              <li className="flex justify-center p-space-md" ref={ref}>
                <Spinner size="md" />
              </li>
            )}
          </Card>
        )}
      </section>
    </section>
  );
};
